import { useCallback, useEffect, useMemo, useState } from "react";
import { ScrollRestoration, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import useVerifyPhoneOTP from "../../hooks/useVerifyPhoneOTP";
import useGetPhoneOTP from "../../hooks/useGetPhoneOTP";
import { HashLoader } from "react-spinners";
import { EnvelopeSimple } from "@phosphor-icons/react";
import OTPInput from "../../components/OTPInput";
import { Link } from "react-router-dom";
import SignUpNavbar from "../../components/SignUpNavbar";
import {
  leaveSetupTask,
  readSetupTaskState,
} from "../../utils/setupTaskReturn";

const VerifyPhoneNumber = () => {
  const navigate = useNavigate();
  const [timer, setTimer] = useState(60);
  const [resendEnabled, setResendEnabled] = useState(false);

  const searchParams = new URLSearchParams(window.location.search);
  const redirect = searchParams.get("redirect");

  const [otp, setOTP] = useState("");

  const { user, refetch } = useAuth();
  const location = useLocation();
  const setup = readSetupTaskState(location.state);

  const isValid = useMemo(() => {
    return (
      otp.length === 4 &&
      otp.split("").every((v) => v !== "" && !isNaN(Number(v)))
    );
  }, [otp]);

  const { validateOTP, validatingOTP, isOTPError, otpError } =
    useVerifyPhoneOTP({
      async onSuccess() {
        // AuthContext never refetches on its own, so pull the verified profile
        // before leaving; otherwise the next screen still sees it unverified.
        await refetch();
        if (setup.returnTo) {
          leaveSetupTask(navigate, location.state);
          return;
        }
        navigate(redirect || "/home");
      },
    });

  const {
    isGetError: isResendError,
    gettingPhoneOTP: resendingOTP,
    getError: resendError,
    getOTP: resendOTP,
  } = useGetPhoneOTP({
    onSuccess() {
      setTimer(60);
      setResendEnabled(false);
    },
  });

  const isError = useMemo(
    () => isOTPError || isResendError,
    [isOTPError, isResendError]
  );

  const error = useMemo(() => otpError || resendError, [otpError, resendError]);

  const isLoading = useMemo(
    () => validatingOTP || resendingOTP,
    [validatingOTP, resendingOTP]
  );

  const handleResend = useCallback(() => {
    resendOTP({});
  }, [resendOTP]);

  const handleSubmit = () => {
    validateOTP({
      code: otp,
    });
  };

  useEffect(() => {
    let interval: any;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prevTimer) => prevTimer - 1);
      }, 1000);
    } else {
      clearInterval(interval);
      setResendEnabled(true);
    }
    return () => clearInterval(interval);
  }, [timer]);

  return (
    <div>
      <ScrollRestoration />
      {isLoading && (
        <div className="fixed top-0 z-50 left-0 h-screen w-screen flex items-center justify-center bg-black/20 backdrop-blur-sm">
          <HashLoader />
        </div>
      )}
      <div>
        <SignUpNavbar />
        <div className="flex flex-col justify-center items-center p-20 px-5 md:px-20 gap-8 text-lg">
          <div className="p-5 bg-gray-100 rounded-full">
            <EnvelopeSimple size={30} />
          </div>
          <div>
            <div className="font-medium text-center max-w-lg">
              Please check your SMS or Whatsapp.
            </div>
            <div className="text-gray-500 text-center max-w-lg">
              {user?.phone_number
                ? `We've sent a code to ${user?.phone_number}`
                : "We've sent a code to your phone number"}
            </div>
          </div>
          <div className="w-full flex-col gap-5 flex items-center justify-center text-base">
            <OTPInput onChange={(otp) => setOTP(otp)} length={4} />
            {!resendEnabled && (
              <div className="text-gray-500">Resend in {timer} seconds</div>
            )}
            {resendEnabled && (
              <div className="text-gray-500">
                Didn’t get a code?{" "}
                <span
                  className="underline"
                  onClick={handleResend}
                  style={{ cursor: "pointer" }}
                >
                  Click to resend.
                </span>
              </div>
            )}
            <div className="flex flex-col gap-2 max-w-md w-full items-center">
              {isError && (
                <div className="text-red-500 text-center w-full">
                  {error?.message}
                </div>
              )}
              <button
                disabled={!isValid}
                onClick={handleSubmit}
                className="p-4 px-5 w-full border-visaro-gray-700 border rounded-lg font-bold bg-deepBlue text-white text-sm"
              >
                Confirm
              </button>
            </div>
            <div className="flex flex-col gap-2 max-w-md w-full">
              <Link
                to={setup.returnTo || redirect || "/home"}
                state={setup.returnTo ? setup.returnState ?? null : undefined}
                replace={!!setup.returnTo}
                className="text-center text-gray-600"
              >
                Skip <span className="text-visaro-orange">Verify Later</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyPhoneNumber;
